import { useCallback, useEffect, useRef } from 'react'
import { applyShoppingListUpdateReducer, buildShoppingListSignature } from '../utils/shoppingListState.js'
import { dispatchShoppingListAction } from '../utils/shoppingListActions.js'

export function useShoppingListStateMachine({
  groceryListItems,
  loadedSavedListId,
  loadedSavedListName,
  loadedSavedListIsRecipe,
  loadedSavedListOriginalItemNames,
  setGroceryListItems,
  setLoadedSavedListId,
  setLoadedSavedListName,
  setLoadedSavedListIsRecipe,
  setLoadedSavedListOriginalItemNames,
  setActiveGroceryTab,
  useUnifiedShoppingView,
  mapDisplayItems,
}) {
  const groceryListItemsRef = useRef([])
  const loadedSavedListIdRef = useRef(null)
  const loadedSavedListNameRef = useRef(null)
  const loadedSavedListIsRecipeRef = useRef(false)
  const loadedSavedListOriginalItemNamesRef = useRef(null)
  const loadedListSignatureRef = useRef('')
  const dispatchListActionRef = useRef(null)

  useEffect(() => {
    groceryListItemsRef.current = groceryListItems
  }, [groceryListItems])

  useEffect(() => {
    loadedSavedListIdRef.current = loadedSavedListId
  }, [loadedSavedListId])

  useEffect(() => {
    loadedSavedListNameRef.current = loadedSavedListName
  }, [loadedSavedListName])

  useEffect(() => {
    loadedSavedListIsRecipeRef.current = loadedSavedListIsRecipe
  }, [loadedSavedListIsRecipe])

  useEffect(() => {
    loadedSavedListOriginalItemNamesRef.current = loadedSavedListOriginalItemNames
  }, [loadedSavedListOriginalItemNames])

  const applyShoppingListUpdate = useCallback(({
    items,
    listId = null,
    listName = null,
    isRecipe = false,
    mode = 'replace', // replace | append | clear | refresh_metadata
    preserveCurrentOnSameList = false,
    refreshedItems = [],
  }) => {
    const mapped = mode === 'clear' || mode === 'refresh_metadata'
      ? []
      : mapDisplayItems(items || [])
    const listSignature = mode === 'clear' ? '' : `${listId ?? 'none'}:${buildShoppingListSignature(mapped)}`

    const currentState = {
      groceryListItems: groceryListItemsRef.current,
      loadedSavedListId: loadedSavedListIdRef.current,
      loadedSavedListName: loadedSavedListNameRef.current,
      loadedSavedListIsRecipe: loadedSavedListIsRecipeRef.current,
      loadedSavedListOriginalItemNames: loadedSavedListOriginalItemNamesRef.current,
      loadedListSignature: loadedListSignatureRef.current,
    }

    const { didChange, next } = applyShoppingListUpdateReducer(currentState, {
      items: mapped,
      listId,
      listName,
      isRecipe,
      mode,
      preserveCurrentOnSameList,
      refreshedItems,
      loadedListSignature: loadedListSignatureRef.current,
    })

    if (!didChange) return

    setGroceryListItems(next.groceryListItems)
    setLoadedSavedListId(next.loadedSavedListId)
    setLoadedSavedListName(next.loadedSavedListName)
    setLoadedSavedListIsRecipe(next.loadedSavedListIsRecipe)
    setLoadedSavedListOriginalItemNames(next.loadedSavedListOriginalItemNames)

    if (mode === 'refresh_metadata') return

    if (mode === 'clear') {
      loadedListSignatureRef.current = ''
      return
    }

    if (mode === 'replace') {
      loadedListSignatureRef.current = listSignature
    }

    setActiveGroceryTab(useUnifiedShoppingView ? 'shopping' : 'grocery-list')
  }, [
    mapDisplayItems,
    setActiveGroceryTab,
    setGroceryListItems,
    setLoadedSavedListId,
    setLoadedSavedListIsRecipe,
    setLoadedSavedListName,
    setLoadedSavedListOriginalItemNames,
    useUnifiedShoppingView,
  ])

  const dispatchListAction = useCallback((action) => {
    dispatchShoppingListAction(action, applyShoppingListUpdate)
  }, [applyShoppingListUpdate])

  useEffect(() => {
    dispatchListActionRef.current = dispatchListAction
  }, [dispatchListAction])

  return {
    dispatchListAction,
    dispatchListActionRef,
    loadedSavedListIdRef,
  }
}
